import React from 'react';

import { faCloudArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { SimulateGatewayPassFormSchemaValues } from 'src/features/actions/api/useSimulateGatewayPass';
import { actionsMessages } from 'src/features/actions/utils/messages';
import { useListGateways } from 'src/features/gateway/api/useListGateways';
import { GATEWAY_IDENTIFIER_TYPE_MAP } from 'src/features/gateway/utils/constants';
import { REQUIRED_IDENTIFIER_TYPES } from 'src/features/gateway/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import { GeneralBlock, GeneralBlockBody } from 'src/registry/components/blocks/GeneralBlock';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormMessage, FormField } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { transliterateUppercase } from 'src/utils/regex';

type SimulateLPRDetectionFormProps = {
    form: UseFormReturn<SimulateGatewayPassFormSchemaValues>;
    onSubmit: () => void;
};

type FileFieldProps<T extends keyof SimulateGatewayPassFormSchemaValues> = {
    fieldName: T;
    fieldLabel: { id: string; defaultMessage: string };
};

function SimulateLPRDetectionForm({ onSubmit, form }: SimulateLPRDetectionFormProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();
    const { data: gatewaysData } = useListGateways({ parkingId });

    const identifierType = form.watch('identifier_type');

    React.useEffect(() => {
        if (form.formState.isDirty) {
            form.setValue('identifier_value', undefined);
        }
    }, [identifierType, form]);

    const [uploadedFiles, setUploadedFiles] = React.useState<Record<string, File[]>>({});

    const handleFileChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        fieldName: keyof SimulateGatewayPassFormSchemaValues,
    ) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const filesArray = Array.from(files);

        form.setValue(fieldName, filesArray, {
            shouldValidate: true,
            shouldDirty: true,
        });

        setUploadedFiles((prev) => ({
            ...prev,
            [fieldName]: filesArray,
        }));
    };

    const getFileUploadFormFieldBlock = <T extends keyof SimulateGatewayPassFormSchemaValues>({
        fieldName,
        fieldLabel,
    }: FileFieldProps<T>) => {
        return (
            <FormField
                control={form.control}
                name={fieldName}
                render={() => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(fieldLabel)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <label htmlFor={fieldName} className="w-full cursor-pointer">
                                    <GeneralBlock>
                                        <GeneralBlockBody className="text-primary-700 flex w-full items-center justify-center font-bold">
                                            <div className="flex flex-col items-center">
                                                <FontAwesomeIcon
                                                    icon={faCloudArrowUp}
                                                    size="lg"
                                                    className="text-primary-700"
                                                />
                                                <span>{formatMessage(actionsMessages.clickToUpload)}</span>

                                                {uploadedFiles[fieldName]?.length > 0 && (
                                                    <div className="mt-1 flex flex-col gap-1">
                                                        {uploadedFiles[fieldName].map((file, idx) => (
                                                            <span
                                                                key={idx}
                                                                className="text-sm font-semibold text-gray-500"
                                                            >
                                                                {file.name}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            <input
                                                type="file"
                                                accept=".jpg, .jpeg, .png, .gif, .svg"
                                                id={fieldName}
                                                className="hidden"
                                                multiple
                                                onChange={(e) => handleFileChange(e, fieldName)}
                                            />
                                        </GeneralBlockBody>
                                    </GeneralBlock>
                                </label>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        );
    };
    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="gateway"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...actionsMessages.point} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value ?? ''}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={`${formatMessage(actionsMessages.point)}...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {gatewaysData?.results?.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {
                                                    option.translations.find(
                                                        (translation) => translation.language === locale,
                                                    )?.name
                                                }
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <SelectFormField<SimulateGatewayPassFormSchemaValues>
                form={form}
                name="identifier_type"
                label={formatMessage(actionsMessages.identifierType)}
                options={Object.values(REQUIRED_IDENTIFIER_TYPES)}
                optionTranslationMap={GATEWAY_IDENTIFIER_TYPE_MAP}
            />
            <FormField
                control={form.control}
                name="identifier_value"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(actionsMessages.identifier)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    {...field}
                                    onChange={(e) => {
                                        const value =
                                            identifierType === REQUIRED_IDENTIFIER_TYPES.LICENSE_PLATE
                                                ? transliterateUppercase(e.target.value.toLocaleUpperCase())
                                                : e.target.value;
                                        field.onChange(value);
                                    }}
                                    value={field.value ?? ''}
                                    placeholder={`${formatMessage(actionsMessages.identifier)}...`}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            {getFileUploadFormFieldBlock({
                fieldName: 'files',
                fieldLabel: actionsMessages.images,
            })}
        </Form>
    );
}

export default SimulateLPRDetectionForm;
